import React from 'react';
import { doc, updateDoc, deleteDoc, serverTimestamp, addDoc, collection, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { StandardSection, createEntitySectionConfig } from '../common';
import type { TableColumn } from '../common';
import type { ApiMesh, MeshMovement } from '../../types';
import { apiMeshFormFields, meshMovementFormFields } from '../../config/formFields';

interface ApiMeshSectionProps {
  data: {
    apiMeshes: ApiMesh[];
    meshMovements: MeshMovement[];
  };
  operations: {
    // API Mesh operations will be handled directly in this component
  };
  userPermissions: {
    canEditReports: boolean;
    canDeleteReports: boolean;
  };
}

export const ApiMeshSection: React.FC<ApiMeshSectionProps> = ({ data, userPermissions }) => {
  // Table column configurations
  const apiMeshColumns: TableColumn<ApiMesh>[] = [
    {
      key: 'mesh',
      title: 'Mesh',
      width: '10%',
      editable: true,
      editType: 'number',
      onEdit: async (item: ApiMesh, key: string, newValue: any) => {
        await updateDoc(doc(db, 'apiMeshes', item.id), {
          [key]: Number(newValue),
          updatedAt: serverTimestamp()
        });
      }
    },
    {
      key: 'equipmentType',
      title: 'Equipo',
      width: '15%',
      editable: true,
      editType: 'select',
      selectOptions: [
        { value: 'zaranda1', label: 'Zaranda 1' },
        { value: 'zaranda2', label: 'Zaranda 2' },
        { value: 'zaranda3', label: 'Zaranda 3' },
        { value: 'desarenador', label: 'Desarenador' },
        { value: 'desilter', label: 'Desilter' }
      ],
      onEdit: async (item: ApiMesh, key: string, newValue: any) => {
        await updateDoc(doc(db, 'apiMeshes', item.id), {
          [key]: newValue,
          updatedAt: serverTimestamp()
        });
      }
    },
    {
      key: 'stockMinimo',
      title: 'Stock Mín.',
      width: '10%',
      editable: true,
      editType: 'number',
      onEdit: async (item: ApiMesh, key: string, newValue: any) => {
        await updateDoc(doc(db, 'apiMeshes', item.id), {
          [key]: Number(newValue),
          updatedAt: serverTimestamp()
        });
      }
    },
    {
      key: 'stockActual',
      title: 'Stock Actual',
      width: '12%',
      render: (value, item) => {
        const isLow = value <= (item?.stockMinimo || 5);
        return (
          <span style={{
            color: isLow ? '#ff5722' : '#4caf50',
            fontWeight: 'bold'
          }}>
            {value} {isLow && '⚠️'}
          </span>
        );
      }
    },
    {
      key: 'ubicacion',
      title: 'Ubicación',
      width: '15%',
      editable: true,
      editType: 'text',
      onEdit: async (item: ApiMesh, key: string, newValue: any) => {
        await updateDoc(doc(db, 'apiMeshes', item.id), {
          [key]: newValue,
          updatedAt: serverTimestamp()
        });
      }
    },
    {
      key: 'proveedor',
      title: 'Proveedor',
      width: '18%',
      editable: true,
      editType: 'text',
      onEdit: async (item: ApiMesh, key: string, newValue: any) => {
        await updateDoc(doc(db, 'apiMeshes', item.id), {
          [key]: newValue,
          updatedAt: serverTimestamp()
        });
      },
      render: (value) => value || '(Sin definir)'
    }
  ];

  // CRUD Handlers for API Meshes
  const handleApiMeshSubmit = async (formData: Record<string, any>) => {
    await addDoc(collection(db, 'apiMeshes'), {
      mesh: Number(formData.mesh),
      equipmentType: formData.equipmentType,
      stockMinimo: Number(formData.stockMinimo),
      stockActual: Number(formData.stockActual),
      ubicacion: formData.ubicacion || '',
      proveedor: formData.proveedor || '',
      observaciones: formData.observaciones || '',
      active: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  };

  const handleApiMeshToggle = async (item: ApiMesh) => {
    await updateDoc(doc(db, 'apiMeshes', item.id), {
      active: !item.active,
      updatedAt: serverTimestamp()
    });
  };

  const handleApiMeshDelete = async (item: ApiMesh) => {
    if (window.confirm(`¿Estás seguro de eliminar la malla API ${item.mesh}?`)) {
      await deleteDoc(doc(db, 'apiMeshes', item.id));
    }
  };

  // CRUD Handlers for Mesh Movements
  const handleMeshMovementSubmit = async (formData: Record<string, any>) => {
    await addDoc(collection(db, 'meshMovements'), {
      fecha: formData.fecha,
      meshId: formData.meshId,
      movementType: formData.movementType,
      cantidad: Number(formData.cantidad),
      equipmentId: formData.equipmentId || '',
      operatorName: formData.operatorName || '',
      observaciones: formData.observaciones || '',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    // Update mesh stock
    if (formData.meshId) {
      const meshRef = doc(db, 'apiMeshes', formData.meshId);
      const meshDoc = await getDoc(meshRef);

      if (meshDoc.exists()) {
        const currentStock = meshDoc.data().stockActual || 0;
        let newStock = currentStock;

        switch (formData.movementType) {
          case 'recibido':
          case 'devuelto':
            newStock += Number(formData.cantidad);
            break;
          case 'transferido':
          case 'consumido':
            newStock -= Number(formData.cantidad);
            break;
        }

        await updateDoc(meshRef, {
          stockActual: Math.max(0, newStock),
          updatedAt: serverTimestamp()
        });
      }
    }
  };

  const handleMeshMovementUpdate = async (id: string, data: Record<string, any>) => {
    await updateDoc(doc(db, 'meshMovements', id), {
      ...data,
      updatedAt: serverTimestamp()
    });
  };

  const handleMeshMovementDelete = async (id: string) => {
    await deleteDoc(doc(db, 'meshMovements', id));
  };

  // Quick Setup Handler
  const handleApiMeshesQuickSetup = async () => {
    if (window.confirm('¿Cargar inventario estándar de mallas API? Esta acción agregará múltiples registros.')) {
      const standardMeshes = [
        { mesh: 40, equipmentType: 'zaranda1', stockMinimo: 10, stockActual: 25, ubicacion: 'Almacén Principal', proveedor: 'SWACO', observaciones: 'Malla estándar para sólidos grandes' },
        { mesh: 60, equipmentType: 'zaranda1', stockMinimo: 8, stockActual: 20, ubicacion: 'Almacén Principal', proveedor: 'DERRICK', observaciones: 'Separación intermedia' },
        { mesh: 80, equipmentType: 'zaranda2', stockMinimo: 8, stockActual: 18, ubicacion: 'Almacén Principal', proveedor: 'SWACO', observaciones: 'Separación fina' },
        { mesh: 100, equipmentType: 'zaranda2', stockMinimo: 6, stockActual: 15, ubicacion: 'Almacén Principal', proveedor: 'DERRICK', observaciones: 'Alta eficiencia' },
        { mesh: 120, equipmentType: 'zaranda3', stockMinimo: 5, stockActual: 12, ubicacion: 'Almacén Principal', proveedor: 'SWACO', observaciones: 'Separación muy fina' },
        { mesh: 150, equipmentType: 'desarenador', stockMinimo: 4, stockActual: 10, ubicacion: 'Taller', proveedor: 'DERRICK', observaciones: 'Desarenador principal' },
        { mesh: 200, equipmentType: 'desilter', stockMinimo: 4, stockActual: 8, ubicacion: 'Taller', proveedor: 'SWACO', observaciones: 'Separación de finos' }
      ];

      try {
        for (const mesh of standardMeshes) {
          await handleApiMeshSubmit({
            ...mesh,
            active: true
          });
        }
        alert('✅ Inventario de mallas API cargado exitosamente');
      } catch (error) {
        console.error('Error cargando mallas API:', error);
        alert('❌ Error cargando inventario. Ver consola para detalles.');
      }
    }
  };

  return (
    <>
      <div style={{ marginBottom: '30px', textAlign: 'center' }}>
        <h2>🔗 Gestión de Mallas API y Inventario</h2>
        <p style={{ color: '#666', marginTop: '10px' }}>
          Control completo del inventario de mallas API con seguimiento de movimientos y alertas de stock
        </p>
      </div>

      {/* API Meshes Inventory */}
      <StandardSection
        config={createEntitySectionConfig(
          'Inventario de Mallas API',
          '📦',
          data.apiMeshes,
          apiMeshFormFields,
          apiMeshColumns,
          handleApiMeshSubmit,
          handleApiMeshToggle,
          handleApiMeshDelete,
          {
            description: 'Gestiona el inventario de mallas API con control de stock, ubicaciones y proveedores',
            quickSetup: {
              title: 'Carga Rápida de Inventario',
              description: 'Carga inventario estándar de mallas API (40-200 mesh) con stocks iniciales y ubicaciones',
              buttonLabel: '📦 Cargar Inventario Estándar',
              onQuickSetup: handleApiMeshesQuickSetup
            }
          }
        )}
      />

      {/* Mesh Movements Tracking */}
      <div style={{ marginTop: '40px' }}>
        <StandardSection
          config={{
            title: 'Movimientos de Mallas',
            icon: '📝',
            description: 'Registra todos los movimientos de mallas: recibidos, transferidos, consumidos y devueltos',
            formFields: meshMovementFormFields,
            onSubmit: handleMeshMovementSubmit,
            data: data.meshMovements,
            columns: [
              { key: 'fecha', title: 'Fecha', width: '120px', editType: 'date' },
              { key: 'meshId', title: 'Malla', width: '150px' },
              { key: 'movementType', title: 'Tipo', width: '120px' },
              { key: 'cantidad', title: 'Cantidad', width: '100px' },
              { key: 'equipmentId', title: 'Equipo', width: '120px' },
              { key: 'observaciones', title: 'Observaciones', width: '200px' }
            ] as TableColumn<MeshMovement>[],
            actions: [
              {
                label: 'Eliminar',
                variant: 'danger',
                onClick: async (item: MeshMovement) => {
                  if (window.confirm('¿Estás seguro de eliminar este movimiento?')) {
                    await handleMeshMovementDelete(item.id);
                  }
                }
              }
            ]
          }}
        />
      </div>

      {/* Stock Summary Section */}
      <div style={{ marginTop: 30, padding: 20, backgroundColor: '#f5f5f5', borderRadius: 8 }}>
        <h3>📊 Resumen de Inventario</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginTop: 16 }}>
          {data.apiMeshes.map(mesh => (
            <div key={mesh.id} style={{
              padding: 16,
              backgroundColor: 'white',
              borderRadius: 8,
              border: mesh.stockActual <= mesh.stockMinimo ? '2px solid #ff5722' : '1px solid #ddd'
            }}>
              <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                API {mesh.mesh}
              </div>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: 8 }}>
                {mesh.equipmentType}
              </div>
              <div style={{
                fontSize: '18px',
                fontWeight: 'bold',
                color: mesh.stockActual <= mesh.stockMinimo ? '#ff5722' : '#4caf50'
              }}>
                {mesh.stockActual} unidades
              </div>
              {mesh.stockActual <= mesh.stockMinimo && (
                <div style={{ fontSize: '11px', color: '#ff5722', marginTop: 4 }}>
                  ⚠️ Stock bajo
                </div>
              )}
            </div>
          ))}
        </div>

        {data.apiMeshes.length === 0 && (
          <div style={{ textAlign: 'center', color: '#666', padding: 20 }}>
            No hay mallas registradas en el inventario
          </div>
        )}
      </div>
    </>
  );
};